import React, { useState, useEffect } from 'react';
import { useVehicle } from '../../contexts/VehicleContext';
import { toast } from 'react-toastify';

const FUEL_TYPES = ['Petrol', 'Diesel', 'CNG', 'Electric', 'Hybrid', 'Other'];

const VehicleManager = ({ onVehicleSelect, compact = false }) => {
  const {
    vehicles, selectedVehicle, loading,
    addVehicle, removeVehicle, selectVehicle,
    fetchMakes, fetchModels, fetchYears, fetchVariants
  } = useVehicle();

  const [showAdd, setShowAdd] = useState(false);
  const [makes, setMakes] = useState([]);
  const [models, setModels] = useState([]);
  const [years, setYears] = useState([]);
  const [variants, setVariants] = useState([]);
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState({
    make: '', model: '', modelId: '', year: '', variant: '',
    fuelType: 'Petrol', registrationNumber: '', nickname: ''
  });

  // Load makes on mount
  useEffect(() => {
    fetchMakes().then(setMakes).catch(() => {});
  }, [fetchMakes]);

  // Load models when make changes
  useEffect(() => {
    if (form.make) {
      fetchModels(form.make).then(setModels).catch(() => setModels([]));
      setForm(f => ({ ...f, model: '', modelId: '', year: '', variant: '' }));
      setYears([]); setVariants([]);
    }
  }, [form.make]);

  // Load years and variants when model changes
  useEffect(() => {
    if (form.make && form.model && form.modelId) {
      fetchYears(form.make, form.model, { modelId: form.modelId }).then(setYears).catch(() => setYears([]));
      fetchVariants(form.make, form.model, '', { modelId: form.modelId }).then(setVariants).catch(() => setVariants([]));
      setForm(f => ({ ...f, year: '', variant: '' }));
    }
  }, [form.make, form.model, form.modelId, fetchYears, fetchVariants]);

  const handleChange = (e) => {
    setForm(f => ({ ...f, [e.target.name]: e.target.value }));
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!form.make || !form.model || !form.year) {
      toast.error('Make, Model, and Year are required');
      return;
    }
    try {
      setSaving(true);
      await addVehicle(form);
      toast.success('Vehicle added!');
      setForm({ make: '', model: '', modelId: '', year: '', variant: '', fuelType: 'Petrol', registrationNumber: '', nickname: '' });
      setShowAdd(false);
    } catch (err) {
      toast.error('Failed to add vehicle');
    } finally {
      setSaving(false);
    }
  };

  const handleSelect = async (vehicle) => {
    await selectVehicle(vehicle);
    if (onVehicleSelect) onVehicleSelect(vehicle);
  };

  const handleRemove = async (vehicleId, e) => {
    e.stopPropagation();
    if (!window.confirm('Remove this vehicle?')) return;
    try {
      await removeVehicle(vehicleId);
      toast.success('Vehicle removed');
    } catch {
      toast.error('Failed to remove vehicle');
    }
  };

  const selectedId = selectedVehicle?._id || selectedVehicle?.id;

  if (compact) {
    // Compact header selector (for catalogue page)
    return (
      <div className="flex items-center gap-2 flex-wrap">
        {vehicles.map(v => {
          const vid = v._id || v.id;
          const isSelected = vid === selectedId;
          return (
            <button
              key={vid}
              onClick={() => handleSelect(v)}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition ${
                isSelected
                  ? 'bg-red-600 text-white border-red-600'
                  : 'bg-white text-gray-700 border-gray-300 hover:border-red-400 hover:text-red-600'
              }`}
            >
              {v.nickname || `${v.make} ${v.model} ${v.year}`}
              {isSelected && <span className="ml-1">✓</span>}
            </button>
          );
        })}
        <button
          onClick={() => setShowAdd(true)}
          className="px-3 py-1.5 rounded-full text-xs font-semibold border border-dashed border-gray-400 text-gray-500 hover:border-red-500 hover:text-red-600 transition"
        >
          + Add Vehicle
        </button>

        {showAdd && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
            <AddVehicleModal
              form={form} makes={makes} models={models} years={years} variants={variants}
              onChange={handleChange} onSubmit={handleAdd} saving={saving}
              onClose={() => setShowAdd(false)}
            />
          </div>
        )}
      </div>
    );
  }

  // Full page vehicle manager
  return (
    <div className="max-w-2xl mx-auto p-4 text-slate-100">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-bold text-slate-100">My Vehicles</h2>
        <button
          onClick={() => setShowAdd(s => !s)}
          className="flex items-center gap-1 px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-semibold hover:bg-red-500 transition"
        >
          {showAdd ? '✕ Cancel' : '+ Add Vehicle'}
        </button>
      </div>

      {/* Add Vehicle Form */}
      {showAdd && (
        <div className="mb-6 bg-slate-900/50 border border-slate-700 rounded-xl p-4">
          <h3 className="font-semibold text-slate-200 mb-3">Add New Vehicle</h3>
          <AddVehicleForm
            form={form} makes={makes} models={models} years={years} variants={variants}
            onChange={handleChange} onSubmit={handleAdd} saving={saving}
          />
        </div>
      )}

      {/* Vehicles List */}
      {loading ? (
        <div className="text-center py-8 text-slate-400">Loading vehicles...</div>
      ) : vehicles.length === 0 ? (
        <div className="text-center py-12 text-slate-400">
          <div className="text-5xl mb-3">🚗</div>
          <p className="font-medium text-slate-200">No vehicles added yet</p>
          <p className="text-sm mt-1 text-slate-400">Add your vehicle to see matching spare parts</p>
        </div>
      ) : (
        <div className="space-y-3">
          {vehicles.map(v => {
            const vid = v._id || v.id;
            const isSelected = vid === selectedId;
            return (
              <div
                key={vid}
                onClick={() => handleSelect(v)}
                className={`flex items-center justify-between p-4 rounded-xl border-2 cursor-pointer transition ${
                  isSelected
                    ? 'border-red-500 bg-red-500/10 shadow-md shadow-red-900/20'
                    : 'border-slate-700 bg-slate-900/55 hover:border-red-400/70 hover:shadow'
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center text-lg ${isSelected ? 'bg-red-500/20' : 'bg-slate-800'}`}>
                    🚗
                  </div>
                  <div>
                    <p className="font-semibold text-slate-100">
                      {v.nickname || `${v.make} ${v.model}`}
                    </p>
                    <p className="text-xs text-slate-400">
                      {v.year}{v.variant ? ` · ${v.variant}` : ''}{v.fuelType ? ` · ${v.fuelType}` : ''}
                      {v.registrationNumber ? ` · ${v.registrationNumber}` : ''}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {isSelected && (
                    <span className="text-xs font-semibold text-red-300 bg-red-500/20 px-2 py-0.5 rounded-full">
                      Selected
                    </span>
                  )}
                  <button
                    onClick={(e) => handleRemove(vid, e)}
                    className="text-slate-500 hover:text-red-400 transition text-lg p-1"
                    title="Remove vehicle"
                  >
                    🗑
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// Shared form fields component
const AddVehicleForm = ({ form, makes, models, years, variants, onChange, onSubmit, saving }) => (
  <form onSubmit={onSubmit} className="space-y-3">
    <div className="grid grid-cols-2 gap-3">
      <div>
        <label className="text-xs font-medium text-slate-300">Make *</label>
        <select name="make" value={form.make} onChange={onChange} required
          className="w-full mt-1 border border-slate-600 bg-slate-900 text-slate-100 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-red-500/60 focus:outline-none">
          <option value="">Select Make</option>
          {makes.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
        </select>
      </div>
      <div>
        <label className="text-xs font-medium text-slate-300">Model *</label>
        <select name="model" value={form.modelId} onChange={(e) => {
          const selectedId = e.target.value;
          const selected = models.find((m) => String(m.modelId || '') === String(selectedId));
          onChange({
            target: {
              name: 'modelId',
              value: selectedId
            }
          });
          onChange({
            target: {
              name: 'model',
              value: selected?.label || ''
            }
          });
          onChange({ target: { name: 'year', value: '' } });
          onChange({ target: { name: 'variant', value: '' } });
        }} required disabled={!form.make}
          className="w-full mt-1 border border-slate-600 bg-slate-900 text-slate-100 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-red-500/60 focus:outline-none disabled:bg-slate-800 disabled:text-slate-500">
          <option value="">Select Model</option>
          {models.map(m => <option key={m.modelId} value={m.modelId}>{m.label}</option>)}
        </select>
      </div>
      <div>
        <label className="text-xs font-medium text-slate-300">Year *</label>
        <select name="year" value={form.year} onChange={onChange} required disabled={!form.modelId}
          className="w-full mt-1 border border-slate-600 bg-slate-900 text-slate-100 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-red-500/60 focus:outline-none disabled:bg-slate-800 disabled:text-slate-500">
          <option value="">Select Year</option>
          {years.map(y => <option key={y.value} value={y.value}>{y.label}</option>)}
        </select>
      </div>
      <div>
        <label className="text-xs font-medium text-slate-300">Variant</label>
        <select name="variant" value={form.variant} onChange={onChange} disabled={!form.modelId}
          className="w-full mt-1 border border-slate-600 bg-slate-900 text-slate-100 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-red-500/60 focus:outline-none disabled:bg-slate-800 disabled:text-slate-500">
          <option value="">Select Variant</option>
          {variants.map(v => <option key={v.value} value={v.value}>{v.label}</option>)}
        </select>
      </div>
      <div>
        <label className="text-xs font-medium text-slate-300">Fuel Type</label>
        <select name="fuelType" value={form.fuelType} onChange={onChange}
          className="w-full mt-1 border border-slate-600 bg-slate-900 text-slate-100 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-red-500/60 focus:outline-none">
          {FUEL_TYPES.map(f => <option key={f} value={f}>{f}</option>)}
        </select>
      </div>
      <div>
        <label className="text-xs font-medium text-slate-300">Registration No.</label>
        <input name="registrationNumber" value={form.registrationNumber} onChange={onChange}
          placeholder="MH 01 AB 1234"
          className="w-full mt-1 border border-slate-600 bg-slate-900 text-slate-100 placeholder:text-slate-500 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-red-500/60 focus:outline-none" />
      </div>
    </div>
    <div>
      <label className="text-xs font-medium text-slate-300">Nickname (optional)</label>
      <input name="nickname" value={form.nickname} onChange={onChange}
        placeholder='e.g. "My Car", "Family SUV"'
        className="w-full mt-1 border border-slate-600 bg-slate-900 text-slate-100 placeholder:text-slate-500 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-red-500/60 focus:outline-none" />
    </div>
    <button type="submit" disabled={saving}
      className="w-full bg-red-600 hover:bg-red-700 text-white font-semibold py-2 rounded-lg transition disabled:opacity-60">
      {saving ? 'Saving...' : 'Add Vehicle'}
    </button>
  </form>
);

// Modal version of the form
const AddVehicleModal = ({ form, makes, models, years, variants, onChange, onSubmit, saving, onClose }) => (
  <div className="bg-slate-900 border border-slate-700 rounded-2xl shadow-2xl p-6 w-full max-w-md text-slate-100">
    <div className="flex items-center justify-between mb-4">
      <h3 className="text-lg font-bold text-slate-100">Add Vehicle</h3>
      <button onClick={onClose} className="text-slate-500 hover:text-slate-300 text-xl">✕</button>
    </div>
    <AddVehicleForm
      form={form} makes={makes} models={models} years={years} variants={variants}
      onChange={onChange} onSubmit={onSubmit} saving={saving}
    />
  </div>
);

export default VehicleManager;
